'use client'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { PendingAttachments, type PendingFile } from '@/components/issues/issue-attachments'
import { Check, Loader2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ModalData {
  statuses: Array<{ id: number | string; name: string }>
  trackers: Array<{ id: number | string; name: string }>
  priorities: Array<{ id: number | string; name: string }>
  versions: Array<{ id: number | string; name: string }>
  members: Array<{ id: number | string; name: string }>
  categories: Array<{ id: number | string; name: string }>
  projects: Array<{ id: number | string; name: string }>
}

interface FormData {
  subject: string
  description: string
  project_id: string
  tracker_id: string
  status_id: string
  priority_id: string
  assigned_to_id: string
  fixed_version_id: string
  category_id: string
  parent_issue_id: string
  start_date: string
  due_date: string
  estimated_hours: string
  done_ratio: string
  is_private: boolean
  notes: string
}

interface Props {
  submitLabel?: string
  fieldErrors: Record<string, string>
  setFieldErrors: (e: Record<string, string>) => void
  formData: FormData
  setFormData: (next: FormData | ((prev: FormData) => FormData)) => void
  modalData: ModalData | null
  handleProjectChange: (newProjectId: string) => void
  pendingFiles: PendingFile[]
  setPendingFiles: (files: PendingFile[]) => void
  enhancingDescription: boolean
  enhanceDescription: () => void
  loading: boolean
  onCancel: () => void
  onSubmit: () => void
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null
  return <p className="text-xs text-destructive mt-1.5">{msg}</p>
}

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-foreground mb-2">
      {children}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </label>
  )
}

/* Section header inside the single-page form. */
function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="bg-card border border-border rounded-lg p-5 sm:p-6 space-y-5">
      <header className="space-y-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </header>
      {children}
    </section>
  )
}

/* Resolve a stored id (string) → option display name for CustomDropdown's
   currentValue prop. */
function displayName(
  list: Array<{ id: number | string; name: string }>,
  value: string,
  fallback = '',
): string {
  if (!value) return fallback
  const hit = list.find(o => String(o.id) === value)
  return hit?.name || fallback
}

export function CreateIssueWizardBody(props: Props) {
  const {
    submitLabel = 'Create Issue',
    fieldErrors,
    setFieldErrors,
    formData,
    setFormData,
    modalData,
    handleProjectChange,
    pendingFiles,
    setPendingFiles,
    enhancingDescription,
    enhanceDescription,
    loading,
    onCancel,
    onSubmit,
  } = props

  const clearFieldError = (field: string) => {
    if (!fieldErrors[field]) return
    const next = { ...fieldErrors }
    delete next[field]
    setFieldErrors(next)
  }

  const projects   = modalData?.projects   ?? []
  const trackers   = modalData?.trackers   ?? []
  const statuses   = modalData?.statuses   ?? []
  const priorities = modalData?.priorities ?? []
  const members    = modalData?.members    ?? []

  return (
    <div className="w-full">
      {/* Body — sectioned form, flows naturally on the page (no inner card) */}
      <div className="w-full space-y-5">

          {/* ─── Basics ─── */}
          <Section
            title="Basics"
            description="What is this issue and which project does it belong to?"
          >
            <div>
              <Label required>Project</Label>
              {projects.length > 0 ? (
                <CustomDropdown
                  label="Project"
                  currentValue={displayName(projects, formData.project_id, '')}
                  placeholder="Pick a project..."
                  options={projects.map(p => ({
                    id: p.id,
                    name: p.name,
                    isSelected: String(p.id) === formData.project_id,
                  }))}
                  onSelect={(id) => {
                    clearFieldError('project_id')
                    handleProjectChange(id === null ? '' : String(id))
                  }}
                />
              ) : (
                <div className="text-sm text-muted-foreground">Loading projects...</div>
              )}
              <FieldError msg={fieldErrors.project_id} />
            </div>

            <div>
              <Label required>Issue type</Label>
              {trackers.length > 0 ? (
                <CustomDropdown
                  label="Issue type"
                  currentValue={displayName(trackers, formData.tracker_id, '')}
                  placeholder="Bug, Feature, Task..."
                  options={trackers.map(t => ({
                    id: t.id,
                    name: t.name,
                    isSelected: String(t.id) === formData.tracker_id,
                  }))}
                  onSelect={(id) => {
                    clearFieldError('tracker_id')
                    setFormData(prev => ({ ...prev, tracker_id: id === null ? '' : String(id) }))
                  }}
                />
              ) : (
                <div className="text-sm text-muted-foreground">Loading types...</div>
              )}
              <FieldError msg={fieldErrors.tracker_id} />
            </div>

            <div>
              <Label required>Subject</Label>
              <Input
                value={formData.subject}
                onChange={e => {
                  clearFieldError('subject')
                  setFormData(prev => ({ ...prev, subject: e.target.value }))
                }}
                placeholder="A short summary of the issue"
                className={cn('h-11', fieldErrors.subject && 'border-destructive focus-visible:ring-destructive')}
                autoFocus
              />
              <FieldError msg={fieldErrors.subject} />
            </div>
          </Section>

          {/* ─── Description & attachments ─── */}
          <Section
            title="Description"
            description="Add detail, steps to reproduce, or attach screenshots."
          >
            <div>
              <Label>Details</Label>
              <MarkdownEditor
                value={formData.description}
                onChange={value => setFormData(prev => ({ ...prev, description: value }))}
                placeholder="What's the issue? Steps to reproduce, expected vs actual, etc."
                height={260}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                onClick={enhanceDescription}
                disabled={!formData.description.trim() || enhancingDescription}
              >
                {enhancingDescription ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enhancing with AI...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Enhance with AI</>
                )}
              </Button>
            </div>

            <div>
              <Label>Attachments</Label>
              <PendingAttachments files={pendingFiles} onChange={setPendingFiles} />
            </div>
          </Section>

          {/* ─── Properties ─── */}
          <Section
            title="Properties"
            description="Status, priority and assignee — optional for now, can be set later."
          >
            {statuses.length > 0 && (
              <div>
                <Label>Status</Label>
                <CustomDropdown
                  label="Status"
                  currentValue={displayName(statuses, formData.status_id, '')}
                  placeholder="Open, In progress, Resolved..."
                  options={statuses.map(s => ({
                    id: s.id,
                    name: s.name,
                    isSelected: String(s.id) === formData.status_id,
                  }))}
                  onSelect={(id) => setFormData(prev => ({ ...prev, status_id: id === null ? '' : String(id) }))}
                />
              </div>
            )}

            {priorities.length > 0 && (
              <div>
                <Label>Priority</Label>
                <CustomDropdown
                  label="Priority"
                  currentValue={displayName(priorities, formData.priority_id, '')}
                  placeholder="Low, Normal, High..."
                  options={priorities.map(p => ({
                    id: p.id,
                    name: p.name,
                    isSelected: String(p.id) === formData.priority_id,
                  }))}
                  onSelect={(id) => setFormData(prev => ({ ...prev, priority_id: id === null ? '' : String(id) }))}
                />
              </div>
            )}

            {members.length > 0 && (
              <div>
                <Label>Assignee</Label>
                <CustomDropdown
                  label="Assignee"
                  currentValue={formData.assigned_to_id ? displayName(members, formData.assigned_to_id, '') : 'Unassigned'}
                  placeholder="Unassigned"
                  options={[
                    { id: null, name: 'Unassigned', isSelected: formData.assigned_to_id === '' },
                    ...members.map(m => ({
                      id: m.id,
                      name: m.name,
                      isSelected: String(m.id) === formData.assigned_to_id,
                    })),
                  ]}
                  onSelect={(id) => setFormData(prev => ({ ...prev, assigned_to_id: id === null ? '' : String(id) }))}
                />
              </div>
            )}
          </Section>

          {/* ─── Planning ─── */}
          <Section
            title="Planning"
            description="When is this happening, and how long do you expect it to take?"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="h-11"
                />
              </div>
              <div>
                <Label>Due date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  className="h-11"
                />
              </div>
            </div>

            <div>
              <Label>Estimated hours</Label>
              <Input
                type="number"
                step="0.25"
                min="0"
                value={formData.estimated_hours}
                onChange={e => setFormData(prev => ({ ...prev, estimated_hours: e.target.value }))}
                placeholder="0.00"
                className="h-11"
              />
            </div>
          </Section>
      </div>

      {/* Footer — single primary action, sits flush with the last section */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="button" onClick={onSubmit} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
